// src/components/InviteNotification.js
import { useState, useEffect } from "react";
import { useAuth } from "../AuthContext";
import { useHousehold } from "../HouseholdContext";
import { getInvitesForEmail, acceptInvite, declineInvite, getHousehold } from "../householdService";

export default function InviteNotification() {
 const { user } = useAuth();
 const { household, setHousehold } = useHousehold();
 const [invites, setInvites] = useState([]);
 const [processing, setProcessing] = useState(null); // invite id being processed
 const [dismissed, setDismissed] = useState([]); // locally dismissed invite ids

 useEffect(() => {
 if (!user?.email) return;
 loadInvites();
 }, [user]);

 async function loadInvites() {
 try {
 const found = await getInvitesForEmail(user.email);
 // Filter out invites for households the user is already in
 const relevant = found.filter(inv => !household || inv.householdId !== household.id);
 setInvites(relevant);
 } catch (err) {
 console.error("Failed to load invites:", err);
 }
 }

 async function handleAccept(invite) {
 setProcessing(invite.id);
 try {
 await acceptInvite(invite, user);
 const newHousehold = await getHousehold(invite.householdId);
 setHousehold(newHousehold);
 setInvites(prev => prev.filter(i => i.id !== invite.id));
 } catch (err) {
 console.error("Failed to accept invite:", err);
 alert("Something went wrong accepting the invite. Please try again.");
 } finally {
 setProcessing(null);
 }
 }

 async function handleDecline(invite) {
 setProcessing(invite.id);
 try {
 await declineInvite(invite.id);
 setInvites(prev => prev.filter(i => i.id !== invite.id));
 } catch (err) {
 console.error("Failed to decline invite:", err);
 } finally {
 setProcessing(null);
 }
 }

 function handleDismiss(inviteId) {
 setDismissed(prev => [...prev, inviteId]);
 }

 const visible = invites.filter(i => !dismissed.includes(i.id));
 if (visible.length === 0) return null;

 return (
 <div style={styles.container}>
 {visible.map(invite => (
 <div key={invite.id} style={styles.banner}>
 <div style={styles.left}>
 <div style={styles.iconWrap}></div>
 <div>
 <div style={styles.title}>
 Household Invite from <strong>{invite.inviterName}</strong>
 </div>
 <div style={styles.subtitle}>
 You've been invited to join <strong>{invite.householdName}</strong>.
 All household repair tasks will be shared with you.
 </div>
 </div>
 </div>
 <div style={styles.actions}>
 <button
 style={styles.acceptBtn}
 onClick={() => handleAccept(invite)}
 disabled={processing === invite.id}
 >
 {processing === invite.id ? "Joining..." : " Accept"}
 </button>
 <button
 style={styles.declineBtn}
 onClick={() => handleDecline(invite)}
 disabled={processing === invite.id}
 >
 Decline
 </button>
 <button
 style={styles.dismissBtn}
 onClick={() => handleDismiss(invite.id)}
 title="Dismiss for now"
 >
 ×
 </button>
 </div>
 </div>
 ))}
 </div>
 );
}

const styles = {
 container: {
 position: "fixed",
 bottom: 24,
 right: 24,
 zIndex: 1000,
 display: "flex",
 flexDirection: "column",
 gap: 12,
 maxWidth: 440,
 width: "calc(100vw - 48px)",
 },
 banner: {
 background: "white",
 borderRadius: 18,
 boxShadow: "0 8px 32px rgba(139,69,19,0.18), 0 2px 8px rgba(0,0,0,0.08)",
 border: "2px solid #fed7aa",
 padding: "16px 18px",
 display: "flex",
 alignItems: "flex-start",
 gap: 14,
 flexDirection: "column",
 animation: "slideUp 0.3s ease",
 },
 left: {
 display: "flex",
 alignItems: "flex-start",
 gap: 12,
 width: "100%",
 },
 iconWrap: {
 fontSize: 32,
 flexShrink: 0,
 background: "#fff7ed",
 borderRadius: 12,
 width: 48,
 height: 48,
 display: "flex",
 alignItems: "center",
 justifyContent: "center",
 },
 title: {
 fontFamily: "'Nunito', sans-serif",
 fontSize: 14,
 fontWeight: 700,
 color: "#2c1810",
 marginBottom: 4,
 },
 subtitle: {
 fontFamily: "'Nunito', sans-serif",
 fontSize: 13,
 color: "#a07850",
 fontWeight: 600,
 lineHeight: 1.5,
 },
 actions: {
 display: "flex",
 gap: 8,
 width: "100%",
 alignItems: "center",
 },
 acceptBtn: {
 flex: 1,
 background: "linear-gradient(135deg, #ff8c00, #f97316)",
 border: "none",
 color: "white",
 padding: "10px 16px",
 borderRadius: 10,
 cursor: "pointer",
 fontFamily: "'Nunito', sans-serif",
 fontSize: 14,
 fontWeight: 800,
 boxShadow: "0 3px 10px rgba(249,115,22,0.3)",
 transition: "all 0.18s",
 },
 declineBtn: {
 flex: 1,
 background: "white",
 border: "2px solid #e8d5b8",
 color: "#7a5c3a",
 padding: "9px 16px",
 borderRadius: 10,
 cursor: "pointer",
 fontFamily: "'Nunito', sans-serif",
 fontSize: 14,
 fontWeight: 700,
 transition: "all 0.18s",
 },
 dismissBtn: {
 background: "none",
 border: "none",
 color: "#c09870",
 fontSize: 20,
 fontWeight: 900,
 cursor: "pointer",
 padding: "0 4px",
 lineHeight: 1,
 flexShrink: 0,
 },
};
